import { ref } from 'vue'
import type { PhotoDetail } from '@/types'
import API from '@/services'
import L from 'leaflet'

export function usePhotos() {
    const photoDetails = ref<PhotoDetail | null>(null)

    async function getPhotoDetails(photoId: number) {
        const response = await API.getPhotoDetails(photoId)
        photoDetails.value = response
    }

    function geoJsonPolygonToBounds(geojsonStr: string): [[number, number], [number, number]] | null {
        try {
            const geojson = JSON.parse(geojsonStr)
            if (geojson.type === "Polygon" && Array.isArray(geojson.coordinates)) {
                const coords = geojson.coordinates[0]
                let minLat = coords[0][1], maxLat = coords[0][1]
                let minLng = coords[0][0], maxLng = coords[0][0]
                coords.forEach(([lng, lat]: [number, number]) => {
                    if (lat < minLat) minLat = lat
                    if (lat > maxLat) maxLat = lat
                    if (lng < minLng) minLng = lng
                    if (lng > maxLng) maxLng = lng
                })
                return [
                    [minLat, minLng],
                    [maxLat, maxLng]
                ]
            }
        } catch (e) {
            return null
        }
        return null
    }

    // Función para parsear cam_polygon y obtener las coordenadas de las esquinas
    function parseCamPolygonCorners(camPolygonString: string): [number, number][] | null {
        try {
            if (!camPolygonString) return null

            const polygon = JSON.parse(camPolygonString)

            if (polygon.type === 'Polygon' && polygon.coordinates && Array.isArray(polygon.coordinates)) {
                const coordinates = polygon.coordinates[0]

                if (Array.isArray(coordinates) && coordinates.length >= 4) {
                    // Convertir de [lon, lat] a [lat, lon] para Leaflet y tomar solo los primeros 4
                    const originalCorners: [number, number][] = coordinates
                        .slice(0, 4)
                        .map(([lon, lat]: [number, number]) => [lat, lon])

                    console.log('Esquinas originales:', originalCorners)

                    // Reordenar: punto 4 → 1, punto 1 → 2, punto 2 → 3, punto 3 → 4
                    const reorderedCorners: [number, number][] = [
                        originalCorners[3],
                        originalCorners[2],
                        originalCorners[1],
                        originalCorners[0]
                    ]

                    console.log('Esquinas reordenadas (4→1, 1→2, 2→3, 3→4):', reorderedCorners)
                    return reorderedCorners
                }
            }

            return null
        } catch (error) {
            console.error('Error parseando cam_polygon:', error)
            return null
        }
    }

    // Función para crear overlay de imagen con transformación de perspectiva
    function createCustomImageOverlay(corners: [number, number][], imageUrl: string) {
        const CustomImageOverlay = L.Layer.extend({
            initialize: function (imageUrl: string, corners: [number, number][]) {
                this._imageUrl = imageUrl
                this._corners = corners
                this._bounds = L.latLngBounds(corners)
            },

            onAdd: function (map: L.Map) {
                this._map = map

                // Crear canvas
                this._canvas = L.DomUtil.create('canvas', 'custom-image-overlay')
                this._canvas.style.position = 'absolute'
                this._canvas.style.pointerEvents = 'auto'
                this._canvas.style.cursor = 'pointer'
                this._canvas.style.zIndex = '100'

                // Obtener contexto del canvas
                this._ctx = this._canvas.getContext('2d')

                // Crear imagen
                this._image = new Image()
                this._image.crossOrigin = 'anonymous'
                this._image.onload = () => {
                    console.log('Imagen cargada, dibujando overlay')
                    this._reset()
                }
                this._image.onerror = () => {
                    console.error('Error cargando imagen:', imageUrl)
                }

                // Agregar canvas al mapa
                map.getPanes().overlayPane.appendChild(this._canvas)

                // Eventos del mapa
                map.on('viewreset', this._reset, this)
                map.on('zoom', this._reset, this)
                map.on('zoomend', this._reset, this)
                map.on('moveend', this._reset, this)

                // Evento click en canvas
                this._canvas.addEventListener('click', () => {
                    window.open(imageUrl, '_blank')
                })

                // Cargar imagen
                this._image.src = imageUrl
            },

            onRemove: function (map: L.Map) {
                if (this._canvas && this._canvas.parentNode) {
                    this._canvas.parentNode.removeChild(this._canvas)
                }
                map.off('viewreset', this._reset, this)
                map.off('zoom', this._reset, this)
                map.off('zoomend', this._reset, this)
                map.off('moveend', this._reset, this)
            },

            _reset: function () {
                if (!this._image || !this._image.complete || !this._canvas || !this._map) return

                try {
                    // Convertir coordenadas de esquinas a píxeles
                    const pixelCorners = this._corners.map((corner: [number, number]) =>
                        this._map.latLngToLayerPoint(L.latLng(corner[0], corner[1]))
                    )

                    // Calcular bounds del canvas expandido
                    const xs = pixelCorners.map((p: { x: number; y: number }) => p.x)
                    const ys = pixelCorners.map((p: { x: number; y: number }) => p.y)
                    const minX = Math.min(...xs) - 50
                    const minY = Math.min(...ys) - 50
                    const maxX = Math.max(...xs) + 50
                    const maxY = Math.max(...ys) + 50

                    // Dimensionar canvas
                    const width = maxX - minX
                    const height = maxY - minY

                    this._canvas.width = width
                    this._canvas.height = height
                    this._canvas.style.width = width + 'px'
                    this._canvas.style.height = height + 'px'

                    // Posicionar canvas
                    L.DomUtil.setPosition(this._canvas, L.point(minX, minY))

                    // Ajustar coordenadas relativas al canvas
                    const relativeCorners = pixelCorners.map((p: { x: number; y: number }) => ({
                        x: p.x - minX,
                        y: p.y - minY
                    }))

                    // Limpiar canvas
                    this._ctx.clearRect(0, 0, width, height)

                    // Dibujar imagen con transformación de perspectiva
                    this._drawTransformedImage(relativeCorners)

                } catch (error) {
                    console.error('Error en _reset del overlay:', error)
                }
            },

            _drawTransformedImage: function (corners: { x: number, y: number }[]) {
                try {
                    if (corners.length !== 4) {
                        console.error('Se necesitan exactamente 4 esquinas para la transformación')
                        return
                    }

                    // Configurar contexto
                    this._ctx.globalAlpha = 0.8
                    this._ctx.imageSmoothingEnabled = true

                    // Método: Dividir la imagen en triángulos y aplicar transformaciones
                    this._drawImageWithPerspective(corners)

                } catch (error) {
                    console.error('Error dibujando imagen transformada:', error)
                }
            },

            _drawImageWithPerspective: function (corners: { x: number, y: number }[]) {
                try {
                    // Crear un canvas temporal para la imagen original
                    const tempCanvas = document.createElement('canvas')
                    const tempCtx = tempCanvas.getContext('2d')
                    tempCanvas.width = this._image.width
                    tempCanvas.height = this._image.height
                    if (tempCtx) {
                        tempCtx.drawImage(this._image, 0, 0)
                    }

                    // Definir las coordenadas de la imagen original (rectángulo)
                    const imgCorners = [
                        { x: 0, y: 0 },
                        { x: this._image.width, y: 0 },
                        { x: this._image.width, y: this._image.height },
                        { x: 0, y: this._image.height }
                    ]

                    // Dibujar primer triángulo (0,1,2)
                    this._drawTriangle(
                        tempCanvas,
                        [imgCorners[0], imgCorners[1], imgCorners[2]],
                        [corners[0], corners[1], corners[2]]
                    )

                    // Dibujar segundo triángulo (0,2,3)
                    this._drawTriangle(
                        tempCanvas,
                        [imgCorners[0], imgCorners[2], imgCorners[3]],
                        [corners[0], corners[2], corners[3]]
                    )

                    // Dibujar borde del polígono
                    this._ctx.globalAlpha = 0
                    this._ctx.strokeStyle = '#ff6b35'
                    this._ctx.lineWidth = 3
                    this._ctx.beginPath()
                    this._ctx.moveTo(corners[0].x, corners[0].y)
                    this._ctx.lineTo(corners[1].x, corners[1].y)
                    this._ctx.lineTo(corners[2].x, corners[2].y)
                    this._ctx.lineTo(corners[3].x, corners[3].y)
                    this._ctx.closePath()
                    this._ctx.stroke()

                    // Agregar puntos en las esquinas
                    this._ctx.fillStyle = '#ff6b35'
                    corners.forEach((corner, index) => {
                        this._ctx.beginPath()
                        this._ctx.arc(corner.x, corner.y, 6, 0, 2 * Math.PI)
                        this._ctx.fill()

                        this._ctx.fillStyle = 'white'
                        this._ctx.font = 'bold 14px Arial'
                        this._ctx.fillText((index + 1).toString(), corner.x - 4, corner.y + 4)
                        this._ctx.fillStyle = '#ff6b35'
                    })

                } catch (error) {
                    console.error('Error en drawImageWithPerspective:', error)
                }
            },

            _drawTriangle: function (sourceCanvas: HTMLCanvasElement, sourceTriangle: { x: number, y: number }[], destTriangle: { x: number, y: number }[]) {
                try {
                    const transform = this._calculateAffineTransform(sourceTriangle, destTriangle)

                    if (!transform) return

                    this._ctx.save()

                    // Crear clipping path para el triángulo destino
                    this._ctx.beginPath()
                    this._ctx.moveTo(destTriangle[0].x, destTriangle[0].y)
                    this._ctx.lineTo(destTriangle[1].x, destTriangle[1].y)
                    this._ctx.lineTo(destTriangle[2].x, destTriangle[2].y)
                    this._ctx.closePath()
                    this._ctx.clip()

                    // Aplicar transformación
                    this._ctx.transform(transform.a, transform.b, transform.c, transform.d, transform.e, transform.f)

                    // Dibujar la imagen transformada
                    this._ctx.globalAlpha = 1
                    this._ctx.drawImage(sourceCanvas, 0, 0)

                    this._ctx.restore()

                } catch (error) {
                    console.error('Error dibujando triángulo:', error)
                }
            },

            _calculateAffineTransform: function (source: { x: number, y: number }[], dest: { x: number, y: number }[]) {
                try {
                    const [s0, s1, s2] = source
                    const [d0, d1, d2] = dest

                    const denominator = s0.x * (s1.y - s2.y) + s1.x * (s2.y - s0.y) + s2.x * (s0.y - s1.y)

                    if (Math.abs(denominator) < 0.0001) {
                        console.warn('Denominador muy pequeño, usando transformación identidad')
                        return { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 }
                    }

                    const a = (d0.x * (s1.y - s2.y) + d1.x * (s2.y - s0.y) + d2.x * (s0.y - s1.y)) / denominator
                    const b = (d0.y * (s1.y - s2.y) + d1.y * (s2.y - s0.y) + d2.y * (s0.y - s1.y)) / denominator
                    const c = (d0.x * (s2.x - s1.x) + d1.x * (s0.x - s2.x) + d2.x * (s1.x - s0.x)) / denominator
                    const d = (d0.y * (s2.x - s1.x) + d1.y * (s0.x - s2.x) + d2.y * (s1.x - s0.x)) / denominator
                    const e = (d0.x * (s1.x * s2.y - s2.x * s1.y) + d1.x * (s2.x * s0.y - s0.x * s2.y) + d2.x * (s0.x * s1.y - s1.x * s0.y)) / denominator
                    const f = (d0.y * (s1.x * s2.y - s2.x * s1.y) + d1.y * (s2.x * s0.y - s0.x * s2.y) + d2.y * (s0.x * s1.y - s1.x * s0.y)) / denominator

                    return { a, b, c, d, e, f }

                } catch (error) {
                    console.error('Error calculando transformación afín:', error)
                    return { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 }
                }
            },

            getBounds: function () {
                return this._bounds
            }
        })

        const overlay = new CustomImageOverlay()
        overlay.initialize(imageUrl, corners)
        return overlay
    }

    return {
        photoDetails,
        getPhotoDetails,
        geoJsonPolygonToBounds,
        parseCamPolygonCorners,
        createCustomImageOverlay
    }
}